import { useAuthStore } from '@/store/authStore';
import { useUserPreference } from '@/hooks/useUserPreference';
import { getWelcomeDismissedAt } from '@/lib/onboardingTours/utils';

const ANNOUNCEMENT_DELAY_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * Check if enough time has passed since welcome was dismissed
 * Returns true if announcements should be suppressed (within delay period)
 */
function isWithinAnnouncementDelayPeriod(userId: string | undefined): boolean {
  if (!userId) return false;

  const welcomeDismissedAt = getWelcomeDismissedAt(userId);
  if (!welcomeDismissedAt) {
    // No timestamp means either old user (show announcements) or never dismissed (handled by isNewUser)
    return false;
  }

  const dismissedTime = new Date(welcomeDismissedAt).getTime();
  const now = Date.now();
  return now - dismissedTime < ANNOUNCEMENT_DELAY_MS;
}

/**
 * Hook to check if announcements should be suppressed due to onboarding state.
 * Suppresses announcements for:
 * 1. New users who haven't completed onboarding (onboarding_required === 'true')
 * 2. Users who completed onboarding within the last 24 hours
 */
export function useAnnouncementDelay() {
  const { user } = useAuthStore();

  // Check if user is new (has onboarding_required = 'true')
  const { value: onboardingRequired, isLoading: isLoadingOnboarding } = useUserPreference({
    key: 'onboarding_required',
    defaultValue: 'false',
  });

  // Check if within the 24-hour delay period after onboarding
  const isWithinDelayPeriod = isWithinAnnouncementDelayPeriod(user?.id);

  const isNewUser = onboardingRequired === 'true';

  // A user who went through the onboarding welcome flow has a dismissal timestamp.
  // This is a permanent marker — existing users who predate onboarding won't have it.
  const wentThroughOnboarding = isNewUser || (!!user?.id && !!getWelcomeDismissedAt(user.id));

  // Suppress announcements for new users OR users who just completed onboarding (within 24 hours)
  const shouldSuppressAnnouncements = isNewUser || isWithinDelayPeriod;

  return {
    shouldSuppressAnnouncements,
    /** True if the user signed up after onboarding was introduced (permanent marker) */
    wentThroughOnboarding,
    isLoading: isLoadingOnboarding,
  };
}
